import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (body) => {
  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

export const fetchLeasingFuel = createAsyncThunk(
  "leasingFuel/fetchAll",
  async ({ page = 1, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/leasing-request/fuel?page=${page}`;

      if (filters.admin_id) {
        url += `&admin_id=${encodeURIComponent(filters.admin_id)}`;
      }
      if (filters.order_number) {
        url += `&order_number=${encodeURIComponent(filters.order_number)}`;
      }
      if (filters.plate) {
        url += `&plate=${encodeURIComponent(filters.plate)}`;
      }
      if (filters.fuel_type_id) {
        url += `&fuel_type_id=${encodeURIComponent(filters.fuel_type_id)}`;
      }
      if (filters.status) {
        url += `&status=${encodeURIComponent(filters.status)}`;
      }

      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load fuel list";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingFuelRecord = createAsyncThunk(
  "leasingFuel/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/leasing-request/fuel/${id}`, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load fuel record";
      return rejectWithValue(message);
    }
  }
);

const appendFuelFormData = (formData) => {
  const data = new FormData();
  if (formData.admin_id) data.append("admin_id", formData.admin_id);
  if (formData.date_of_filling) data.append("date_of_filling", formData.date_of_filling);
  if (formData.vehicle_id) data.append("vehicle_id", formData.vehicle_id);
  if (formData.fuel_type_id) data.append("fuel_type_id", formData.fuel_type_id);
  if (formData.meter_reading !== undefined && formData.meter_reading !== null) {
    data.append("meter_reading", formData.meter_reading);
  }
  if (formData.number_of_liters !== undefined && formData.number_of_liters !== null) {
    data.append("number_of_liters", formData.number_of_liters);
  }
  if (formData.cost !== undefined && formData.cost !== null) {
    data.append("cost", formData.cost);
  }
  if (formData.notes) data.append("notes", formData.notes);

  if (formData.attachments && formData.attachments.length > 0) {
    formData.attachments.forEach((file) => {
      data.append("attachments[]", file);
    });
  }

  return data;
};

export const addLeasingFuel = createAsyncThunk(
  "leasingFuel/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = appendFuelFormData(formData);
      const response = await axios.post(`${BASE_URL}/leasing-request/fuel`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add fuel request";
      return rejectWithValue(message);
    }
  }
);

export const updateLeasingFuel = createAsyncThunk(
  "leasingFuel/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = appendFuelFormData(formData);
      const response = await axios.post(`${BASE_URL}/leasing-request/fuel/${id}`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update fuel request";
      return rejectWithValue(message);
    }
  }
);

export const deleteLeasingFuel = createAsyncThunk(
  "leasingFuel/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/leasing-request/fuel/${id}`, {
        headers: getHeaders(),
      });
      return { id, message: response.data.message || "Deleted successfully" };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete fuel request";
      return rejectWithValue(message);
    }
  }
);

export const fetchFuelTypes = createAsyncThunk(
  "leasingFuel/fetchFuelTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/fuel-types`, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load fuel types";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingEmployees = createAsyncThunk(
  "leasingFuel/fetchEmployees",
  async (search = "", { rejectWithValue }) => {
    try {
      const url = search
        ? `${BASE_URL}/settings/employees?search=${encodeURIComponent(search)}`
        : `${BASE_URL}/settings/employees`;
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load employees";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingVehicles = createAsyncThunk(
  "leasingFuel/fetchVehicles",
  async (search = "", { rejectWithValue }) => {
    try {
      const url = search
        ? `${BASE_URL}/settings/vehicles?search=${encodeURIComponent(search)}`
        : `${BASE_URL}/settings/vehicles`;
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicles";
      return rejectWithValue(message);
    }
  }
);

const LeasingFuelSlice = createSlice({
  name: "leasingFuel",
  initialState: {
    fuel: [],
    fuelRecord: null,
    pagination: null,
    fuelTypes: [],
    employees: [],
    vehicles: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeasingFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeasingFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fuel = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchLeasingFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeasingFuelRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeasingFuelRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fuelRecord = action.payload;
      })
      .addCase(fetchLeasingFuelRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addLeasingFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addLeasingFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Added successfully";
      })
      .addCase(addLeasingFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLeasingFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLeasingFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Updated successfully";
      })
      .addCase(updateLeasingFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteLeasingFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLeasingFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fuel = state.fuel.filter((item) => item.id !== action.payload.id);
        state.success = action.payload.message || "Deleted successfully";
      })
      .addCase(deleteLeasingFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFuelTypes.fulfilled, (state, action) => {
        state.fuelTypes = action.payload;
      })
      .addCase(fetchLeasingEmployees.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      .addCase(fetchLeasingVehicles.fulfilled, (state, action) => {
        state.vehicles = action.payload;
      });
  },
});

export const { clearState } = LeasingFuelSlice.actions;
export default LeasingFuelSlice.reducer;

